import { Plus } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { PageShell } from "../../../components/common";
import { useGetNewsList } from "../hooks/useGetNewsList";
import { useGetNewsStatistics } from "../hooks/useGetNewsStatistics";
import { NewsListDataTable } from "../components/data-table";
import NewsStatisticsSection, { buildNewsStatisticsCards } from "../components/NewsStatisticsSection";
import { NewsToolbar } from "../components/NewsToolbar";

function NewListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    news,
    loading,
    error,
    filters,
    updateFilter,
    sorting,
    updateSorting,
    page,
    setPage,
    totalCount,
    itemsPerPage,
    refreshNews,
  } = useGetNewsList();
  const { statistics: newsStatistics, loading: statsLoading, refresh: refreshNewsStatistics } =
    useGetNewsStatistics();

  const weekAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  }, []);

  const thisWeekCount = useMemo(
    () => news.filter((item) => item.created_at && new Date(item.created_at) > weekAgo).length,
    [news, weekAgo],
  );

  const statsWithIcons = useMemo(
    () =>
      buildNewsStatisticsCards(
        t("news.total"),
        t("news.this_week"),
        newsStatistics?.total ?? totalCount,
        newsStatistics?.this_week ?? thisWeekCount,
      ),
    [newsStatistics, thisWeekCount, totalCount, t],
  );

  const handleRefresh = async () => {
    await Promise.all([refreshNews(), refreshNewsStatistics()]);
  };

  const handleKeywordChange = (nextKeyword: string) => {
    updateFilter("keyword", nextKeyword || undefined);
  };

  const handleOpenNews = (newsId: string, item: (typeof news)[number]) => {
    navigate(`/news/view/${newsId}`, { state: { news: item } });
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <PageShell
      title={t("news.center")}
      subtitle={t("news.subtitle")}
      actions={
        <Button
          onClick={() => navigate("/news/create")}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("news.create_new_news")}
        </Button>
      }
      stats={<NewsStatisticsSection stats={statsWithIcons} loading={statsLoading} />}
    >
      <NewsToolbar
        keyword={filters.keyword ?? ""}
        loading={loading || statsLoading}
        onKeywordChange={handleKeywordChange}
        onRefresh={() => void handleRefresh()}
      />
      <NewsListDataTable
        news={news}
        loading={loading}
        error={error}
        page={page}
        totalPages={totalPages}
        sortKey={sorting.key}
        sortDirection={sorting.direction}
        onSort={updateSorting}
        onPageChange={(nextPage) => setPage(nextPage)}
        onRetry={() => void handleRefresh()}
        onRowClick={(record) => handleOpenNews(record.id, record)}
        thumbnailLabel={t("news.thumbnail")}
        titleLabel={t("news.title")}
        contentLabel={t("news.content")}
        createdAtLabel={t("news.created_at")}
        updatedAtLabel={t("news.updated_at")}
        loadingLabel={t("news.loading_news")}
        emptyLabel="Currently no news available."
      />
    </PageShell>
  );
}

export default NewListPage;
